/**
 * JobBatchQueue — read-only batch queue for annotators, embedded in active job cards.
 *
 * Per _NORTH-STAR.md §5.2 + invariant #5: annotators see read-only batch cards.
 * No verbs (no Start, no Submit, no Revise). The only annotator interaction
 * is the progress input (items_completed tracking) — when items reach
 * total_items, batchService.updateBatchProgress auto-advances the batch to
 * In Review via advanceIfComplete() (event-driven, no explicit submit click).
 *
 * Visible states: In Progress, In Review (work pipeline for the annotator).
 * Hidden: Delivered, Accepted (done — annotator's job is over).
 *
 * Rejection context: if a batch has rejection_note, it's surfaced inline as
 * read-only context so the annotator understands why they're back on it.
 */
import { useState, useRef, useEffect } from 'react';
import { updateBatchProgress } from '../services/batchService';
import { BATCH_STATES } from '../utils/batchProgress';

const STATUS_COLORS = {
  [BATCH_STATES.IN_PROGRESS]: '#f59e0b', // amber — work happening
  [BATCH_STATES.IN_REVIEW]:   '#8b5cf6', // purple — out of annotator's hands
  [BATCH_STATES.DELIVERED]:   '#5DCAA5', // teal — done (hidden in this view)
  [BATCH_STATES.ACCEPTED]:    '#5DCAA5', // teal — done (hidden in this view)
};

const STATUS_PRIORITY = {
  [BATCH_STATES.IN_PROGRESS]: 0,
  [BATCH_STATES.IN_REVIEW]:   1,
  [BATCH_STATES.DELIVERED]:   2,
  [BATCH_STATES.ACCEPTED]:    3,
};

export default function JobBatchQueue({ batches = [], onRefresh }) {
  const [progressInput, setProgressInput] = useState({});
  const debounceRef = useRef({});

  useEffect(() => {
    const prog = {};
    batches.forEach(b => { prog[b.id] = b.items_completed || 0; });
    setProgressInput(prog);
    // Capture the ref at effect-run time so cleanup clears the timers we
    // registered during this effect pass, not whatever's in the ref later.
    const timers = debounceRef.current;
    return () => { Object.values(timers).forEach(t => clearTimeout(t)); };
  }, [batches]);

  const sorted = [...batches].sort((a, b) => (STATUS_PRIORITY[a.status] ?? 99) - (STATUS_PRIORITY[b.status] ?? 99));
  // Show only work-in-pipeline batches (annotator's active queue). Hide Delivered + Accepted.
  const visible = sorted.filter(b => b.status === BATCH_STATES.IN_PROGRESS || b.status === BATCH_STATES.IN_REVIEW);

  if (visible.length === 0) return null;

  async function handleProgressChange(batchId, value, totalItems) {
    const num = Math.min(parseInt(value) || 0, totalItems);
    setProgressInput(prev => ({ ...prev, [batchId]: num }));
    if (debounceRef.current[batchId]) clearTimeout(debounceRef.current[batchId]);
    debounceRef.current[batchId] = setTimeout(async () => {
      // updateBatchProgress auto-advances to In Review when items_completed reaches total_items.
      const updated = await updateBatchProgress(batchId, num);
      if (onRefresh && updated) await onRefresh();
    }, 800);
  }

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
        Batch Queue ({visible.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {visible.map(b => {
          const color = STATUS_COLORS[b.status] || 'var(--text-muted)';
          const hasRejection = !!b.rejection_note;
          return (
            <div key={b.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              background: hasRejection ? 'var(--bg-error)' : 'var(--bg-stat)',
              borderRadius: 6, border: hasRejection ? '1px solid var(--border-error)' : '1px solid var(--border)',
              flexWrap: 'wrap',
            }}>
              {/* Batch number */}
              <span style={{ fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: 500, color: 'var(--text-primary)', minWidth: 50 }}>
                #{b.batch_number}
              </span>
              {/* Status pill */}
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                color, background: `${color}15`,
                minWidth: 70, textAlign: 'center',
              }}>{b.status}</span>
              {/* Items — editable only while In Progress */}
              {b.status === BATCH_STATES.IN_PROGRESS ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
                  <input
                    type="number"
                    value={progressInput[b.id] ?? b.items_completed}
                    onChange={e => handleProgressChange(b.id, e.target.value, b.total_items)}
                    onClick={e => e.stopPropagation()}
                    min={0} max={b.total_items}
                    style={{
                      width: 48, padding: '2px 4px', fontSize: 11, fontFamily: 'var(--font-mono)',
                      border: '1px solid var(--border)', borderRadius: 4, textAlign: 'center',
                      background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none',
                    }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>/ {b.total_items}</span>
                </div>
              ) : (
                <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', flex: 1 }}>
                  {b.items_completed}/{b.total_items} items
                </span>
              )}
              {/* Right-side status copy */}
              {b.status === BATCH_STATES.IN_REVIEW && (
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Awaiting review</span>
              )}
              {/* Rejection note (full width on wrap) */}
              {hasRejection && (
                <div style={{ flexBasis: '100%', fontSize: 11, color: 'var(--text-error)', paddingTop: 4, lineHeight: 1.4 }}>
                  <strong style={{ fontWeight: 600 }}>Note:</strong> {b.rejection_note}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
